import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Plus, LifeBuoy } from 'lucide-react';
import BrandLogo from '../brand/BrandLogo';
import UserMenu from '../shared/UserMenu';
import ProtectedRoute from '../shared/ProtectedRoute';
import SideNav, { visibleNavGroups } from './SideNav';
import { useAuth } from '../../context/AuthContext';
import { useHelp } from '../../help';

const COLLAPSE_KEY = 'varuna.nav.collapsed';

/**
 * Global application shell — the one navigation frame every signed-in surface
 * shares (map, fleet, vessels, workspace, vetting, admin).
 *
 * Layout: full-height left rail (SideNav) + a right column of top bar over
 * content. The top bar carries orientation (current section), the primary
 * action (Start inspection), help, and the account menu — so the user chrome
 * is ALWAYS visible at the top right, never cut off at the rail's foot on
 * short viewports (the old design's failure mode).
 *
 * On /map the rail auto-collapses to an icon rail and the content column
 * pins to the viewport so the chart keeps its bleed under the bar.
 */
export default function AppShell() {
  const location = useLocation();
  const isMap = location.pathname.startsWith('/map');

  const [userCollapsed, setUserCollapsed] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(COLLAPSE_KEY);
      if (saved !== null) return saved === '1';
      // First visit: iPad-portrait-class widths get the icon rail by
      // default — the full 232px rail squeezes inspection content there.
      return window.matchMedia('(max-width: 1023px)').matches;
    } catch {
      return false;
    }
  });
  // The map defaults to the icon rail so the chart keeps its bleed, but the
  // user can still expand it for this visit; elsewhere the persisted
  // preference applies.
  const [mapExpanded, setMapExpanded] = useState(false);
  const collapsed = isMap ? !mapExpanded : userCollapsed;
  const toggleCollapsed = () => {
    if (isMap) setMapExpanded((v) => !v);
    else setUserCollapsed((v) => !v);
  };

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, userCollapsed ? '1' : '0');
    } catch {
      /* private mode */
    }
  }, [userCollapsed]);

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-slate-950 text-white flex flex-col md:flex-row">
        <SideNav collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />

        {/* Mobile top bar */}
        <MobileTopBar />

        {/* Right column: top bar over content. The map pins the column to the
            viewport (its canvas fills the remainder); everything else scrolls
            with the window, bottom padding clearing the mobile tab nav. */}
        <div className={`flex-1 min-w-0 flex flex-col ${isMap ? 'md:h-screen md:overflow-hidden' : ''}`}>
          <TopBar />
          <main
            id="main-content"
            className={`flex-1 min-w-0 ${isMap ? 'relative min-h-0' : 'pb-20 md:pb-0'}`}
          >
            <Outlet />
          </main>
        </div>

        <MobileBottomNav />
      </div>
    </ProtectedRoute>
  );
}

/** Longest-prefix route → section context shown at the top left. */
const SECTIONS: Array<{ prefix: string; group: string | null; title: string }> = [
  { prefix: '/workspace',                group: 'Operate', title: 'My workspace' },
  { prefix: '/fleet',                    group: 'Operate', title: 'Fleet readiness' },
  { prefix: '/map',                      group: 'Operate', title: 'Live map' },
  { prefix: '/vessels/',                 group: 'Operate', title: 'Vessel profile' },
  { prefix: '/vessels',                  group: 'Operate', title: 'Vessels' },
  { prefix: '/actions',                  group: 'Comply',  title: 'Corrective actions' },
  { prefix: '/vetting/inspections/new',  group: 'Comply',  title: 'New inspection' },
  { prefix: '/vetting/inspections',      group: 'Comply',  title: 'Inspections' },
  { prefix: '/vetting/templates',        group: 'Comply',  title: 'Frameworks' },
  { prefix: '/vetting/settings',         group: null,      title: 'Settings' },
  { prefix: '/admin',                    group: null,      title: 'Administration' },
  { prefix: '/help',                     group: null,      title: 'Help' },
  { prefix: '/inspections',              group: 'Comply',  title: 'Inspections' },
];

function sectionFor(pathname: string) {
  return SECTIONS.find((s) => pathname.startsWith(s.prefix)) ?? null;
}

/**
 * Desktop top bar: section context on the left; primary action, help, and
 * the always-visible account menu on the right.
 */
function TopBar() {
  const { role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { openHelp } = useHelp();
  const section = sectionFor(location.pathname);
  const canInspect = role === 'inspector' || role === 'admin';
  const onNewInspection = location.pathname.startsWith('/vetting/inspections/new');

  return (
    <header className="hidden md:flex h-14 shrink-0 sticky top-0 z-30 items-center justify-between gap-3 px-4 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/70">
      <div className="min-w-0 flex items-baseline gap-2.5">
        {section?.group && (
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-600 font-bold">{section.group}</span>
        )}
        <span className="text-sm font-semibold text-slate-200 truncate">{section?.title ?? 'VarunAIq'}</span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {canInspect && !onNewInspection && (
          <button
            onClick={() => navigate('/vetting/inspections/new')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold shadow-md shadow-cyan-500/25 transition-all duration-fast active:scale-[0.98]"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Start inspection</span>
          </button>
        )}
        <button
          onClick={() => openHelp()}
          aria-label="Help"
          title="Help"
          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800/60 transition-colors"
        >
          <LifeBuoy className="w-[18px] h-[18px]" />
        </button>
        <div className="w-px h-6 bg-slate-800" />
        <UserMenu />
      </div>
    </header>
  );
}

function MobileTopBar() {
  const navigate = useNavigate();
  return (
    <header className="md:hidden sticky top-0 z-30 bg-slate-950/90 backdrop-blur-md border-b border-slate-800 px-3 py-2.5 flex items-center justify-between">
      <button onClick={() => navigate('/')} className="hover:opacity-80 active:scale-[0.98]" aria-label="Home">
        <BrandLogo size="sm" tone="dark" />
      </button>
      <UserMenu />
    </header>
  );
}

/** Bottom tab bar: the first five visible nav items for the current role. */
function MobileBottomNav() {
  const { role } = useAuth();
  const items = visibleNavGroups(role)
    .flatMap((g) => g.items)
    .slice(0, 5);
  if (items.length === 0) return null;

  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-slate-950/95 backdrop-blur-md border-t border-slate-800 grid"
      style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    >
      {items.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          className={({ isActive }) =>
            `min-h-[56px] flex flex-col items-center justify-center gap-0.5 text-[10px] transition-colors active:scale-[0.96] ${
              isActive ? 'text-cyan-300' : 'text-slate-400 hover:text-slate-200'
            }`
          }
        >
          <n.Icon className="w-5 h-5" />
          <span className="leading-none truncate max-w-full px-1">{n.label.split(' ')[0]}</span>
        </NavLink>
      ))}
    </nav>
  );
}
